/** A plan's lifecycle, as the harness persists it. A run the maker paused reads PAUSED; one a step
 * stopped reads FAILED. */
const PLAN_STATES = [
  'DRAFT',
  'READY',
  'EXECUTING',
  'PAUSED',
  'FAILED',
  'COMPLETED',
  'ABANDONED',
] as const

export type HarnessPlanState = (typeof PLAN_STATES)[number]

/** A plan's state, on a plan record and a plan summary alike. */
export type WithPlanState = {
  /** Null for a state this designer does not know yet: the plan is kept, every control that waits
   * for a known state stays off, and those keyed on the run go on working. */
  state: HarnessPlanState | null
  /** The state as the harness sent it, which is what the labels show for an unknown one. */
  reported_state: string
}

/** The state the harness sent, or null when there is none: a plan without one cannot be read. */
export function toPlanState(value: unknown): WithPlanState | null {
  return typeof value === 'string' && value
    ? { state: PLAN_STATES.find((known) => known === value) ?? null, reported_state: value }
    : null
}

const PLAN_STATE_LABEL: Readonly<Record<HarnessPlanState, string>> = {
  DRAFT: 'draft',
  READY: 'ready',
  EXECUTING: 'executing',
  PAUSED: 'paused',
  FAILED: 'run failed',
  COMPLETED: 'completed',
  ABANDONED: 'abandoned',
}

/** The label of a plan's state, lower case as the rail's tags read: `ready`, `run failed`; one this
 * designer does not know yet in the harness's own word, `NEEDS_REVIEW` → `needs review`. */
export function planStateLabelOf(plan: WithPlanState): string {
  return plan.state
    ? PLAN_STATE_LABEL[plan.state]
    : plan.reported_state.toLowerCase().replaceAll('_', ' ')
}
